import React, { useEffect, useState } from "react"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { DragAndDropProvider } from "integrations/dnd"
import { GlobalNotificationContainer } from "themes/default-tailwind/notifications/GlobalNotificationContainer"
import { createBrowserRouter, Outlet } from "react-router-dom"
import { OverlayPanelContainer, SystemInitializingContainer } from "themes/default-tailwind/panels"
import OverlayPanel from "panels/overlay/OverlayPanel"
import { screenSizeAlertModal } from "features/modals/ScreenSizeAlertModal"
import { browserAlertModal } from "features/modals/BrowserAlertModal"
import { WorkspaceConfirmationModalV2Provider } from "features/modals/WorkspaceConfirmationModalV2"
import { PortalledTooltipRoot } from "features/popups/PortalledTooltip"
import { PortalledContextMenuRoot } from "features/popups/PortalledContextMenu"
import { ProjectGate, MapRoute } from "panels/routing/MapRoute"
import { SettingsPasswordRedirect, SettingsRouteNode } from "panels/routing/SettingsRoutes"
import { WorkspaceGate } from "panels/routing/WorkspaceRoutes"
import {
    isRouteErrorResponse,
    RouterProvider,
    UIMatch,
    useLocation,
    useMatches,
    useParams,
    useRouteError,
} from "react-router"
import { datadogRum } from "@datadog/browser-rum"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { useExpiredAuthNotification } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/AuthExpiredProvider"
import Page403 from "panels/routing/Page403"
import {
    WorkspaceExplorerActivityPage,
    WorkspaceExplorerFavoritesPage,
    WorkspaceExplorerRecentsPage,
    WorkspaceExplorerWorkspacePage,
    WorkspaceExplorerWrapper,
} from "panels/routing/WorkspaceExplorerRoutes"
import { LegacyProjectRedirect, LegacyWorkspaceRedirect } from "panels/routing/LegacyRedirects"
import { createRouter, RouteTree } from "@nova/react-router"
import { WorkspaceHome } from "features/data-management/workspace/workspace/body/home/WorkspaceHome"
import Page404 from "./404"
import Page500 from "./_error"
import { MeProvider } from "user/me/MeProvider"
import AuthenticationProvider from "user/authentication/AuthenticationProvider"
import { fleetRouteNode } from "panels/routing/FleetRoutes"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { OverviewPage } from "features/overview/OverviewPage"

export default function RouterWrapper() {
    const [isClient, setIsClient] = useState(false)
    useEffect(() => {
        setIsClient(true)
    }, [])
    if (!isClient) return null
    return <RouterProvider router={getRouter()} />
}

function HomePage() {
    useEffect(() => {
        browserAlertModal()
        screenSizeAlertModal()
    }, [])
    useExpiredAuthNotification()

    const location = useLocation()
    const matches = useMatches() as UIMatch<unknown, { pathname: string; params: string }>[]
    useEffectUnsafe(() => {
        if (matches.length) {
            const match = matches.at(-1)
            if (match) {
                const { pathname = "", params = "" } = match
                let cleanPath = pathname
                for (const [key, value] of Object.entries(params)) {
                    if (value) {
                        cleanPath = cleanPath.replace(value, `:${key}`)
                    }
                }
                datadogRum.startView({ name: cleanPath })
                return
            }
        }
        datadogRum.startView({ name: location.pathname })
    }, [matches, location.pathname])

    return (
        <AuthenticationProvider>
            <MeProvider>
                <DragAndDropProvider>
                    <WorkspaceConfirmationModalV2Provider>
                        <div
                            id="cursor-nova"
                            style={{ cursor: `url("/images/cursors/cursor.svg"), auto` }}
                        >
                            <div id="portal" />
                            <GlobalNotificationContainer />
                            <PortalledTooltipRoot />
                            <PortalledContextMenuRoot />
                            <Outlet />
                            <OverlayPanelContainer>
                                <OverlayPanel />
                            </OverlayPanelContainer>
                        </div>
                    </WorkspaceConfirmationModalV2Provider>
                </DragAndDropProvider>
            </MeProvider>
        </AuthenticationProvider>
    )
}

export async function getServerSideProps({ locale }: { locale: string }) {
    // a development specific feature that allows us to inject dumped state from a file. will only work locally!
    // @todo -- review this, as our store setup has changed!
    // const initialReduxState = (await readStateDump()) || {}

    return {
        props: {
            ...(await serverSideTranslations(locale)),
            // initialReduxState,
        },
    }
}

const routeTree = {
    "/": {
        component: HomePage,
        index: LegacyWorkspaceRedirect,
        errorComponent: ErrorPage,
        children: {
            workspace: {
                index: LegacyWorkspaceRedirect,
                children: {
                    workspaceId: {
                        param: true,
                        component: WorkspaceGate,
                        index: WorkspaceHome,
                        children: {
                            overview: {
                                component: OverviewPage,
                            },
                            explore: {
                                component: WorkspaceExplorerWrapper,
                                index: WorkspaceExplorerRecentsPage,
                                children: {
                                    favorites: { component: WorkspaceExplorerFavoritesPage },
                                    activity: { component: WorkspaceExplorerActivityPage },
                                    ws: {
                                        children: {
                                            "*": { component: WorkspaceExplorerWorkspacePage },
                                        },
                                    },
                                },
                            },
                            settings: SettingsRouteNode,
                            fleet: fleetRouteNode,
                        },
                    },
                },
            },
            "403": {
                component: Page403,
            },
            "settings/password": {
                component: SettingsPasswordRedirect,
            },
            "workspace/:workspaceId/project/:projectId": {
                component: () => <ProjectGate />,
                index: () => <MapRoute />,
            },
            "workspace/:workspaceId/settings/map-snapshot": {
                component: () => <ProjectGate isSnapshot />,
                index: () => <MapRoute isSnapshot />,
            },
        },
    },
    project: {
        component: Outlet,
        index: LegacyProjectRedirect,
        children: {
            projectId: {
                param: true,
                component: ProjectRedirect,
            },
        },
    },
} satisfies RouteTree

const { routes, useRouter } = createRouter(routeTree)
// `routes` is exported so nocha tests can walk the real route tree and mount a component under the
// real gate chain (HomePage → ProjectGate) instead of a hand-built one.
export { routes, useRouter }

export type NovaRouteTree = typeof routeTree

let router: ReturnType<typeof createBrowserRouter>
const getRouter = () => {
    if (!router && typeof window !== "undefined") {
        router = createBrowserRouter(routes)
    }
    return router
}

function ProjectRedirect() {
    const router = useRouter("/")
    const projectId = useParams().projectId!
    useEffectUnsafe(() => {
        ;(async () => {
            try {
                const orgId = await fetchApiGateway("get /org/idFromProjectId/:projectId", {
                    params: { projectId },
                })
                if (!orgId) throw new Error("invalid orgId")
                router.navigate({
                    path: "/workspace/:workspaceId/project/:projectId",
                    params: { workspaceId: orgId, projectId },
                })
            } catch {
                router.navigate({ path: "/" })
            }
        })()
    }, [])
    return <SystemInitializingContainer />
}

function ErrorPage() {
    const error = useRouteError()
    if (isRouteErrorResponse(error)) {
        if (error.status === 403) {
            return <Page403 />
        }
        if (error.status === 404) {
            return <Page404 />
        }
    }
    return <Page500 />
}
